import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { translate as translateBase } from 'loankeeper-common/helpers'
import {
  CollateralSearchParams,
  HypothecCollateral,
  HypothecCollateralsRequestServerData,
  CollateralViewParams,
  CollateralAddParams,
  CollateralAddPermissionParams,
  CollateralDeletePermissionParams,
  CollateralOperationsParams,
  CollateralVisaParams,
  CollateralUndoVisaParams,
  CollateralUpdateAgreementNumberParams,
  CollateralEditStateParams,
  CollateralEditDetailStateParams,
  CollateralUpdateDescParams,
  CollateralManagementViewSessionsParams,
  CollateralManagementPrintSessionsParams,
  CollateralManagementViewWorklistParams,
  CollateralManagementLoadWorklistParams,
  CollateralManagementClearWorklistParams,
  CollateralManagementAddWorklistParams,
  CollateralManagementDeleteWorklistParams,
  CollateralManagementProcessWorklistParams,
  CollateralManagementViewActions,
} from '@lk/api'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { ThunkApi } from '../index.d'

export const loadHypothecCollaterals = createAsyncThunk<HypothecCollateral, CollateralSearchParams, ThunkApi>(
  '[COLLATERAL] LOAD_HYPOTHEC_COLLATERALS',
  async (params, { dispatch, extra, rejectWithValue, signal }) => {
    const { instanceName } = params
    if (instanceName) {
      delete params.instanceName
    }
    const result = await extra.api.collateral.collateralSearch({ ...params }, signal)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue(result.error.message)
    }

    if (instanceName) {
      return {
        instanceName: instanceName,
        data: result.data,
      }
    }

    return result.data
  },
)

export const collateralSearchAgain = createAsyncThunk<HypothecCollateral, string, ThunkApi>(
  '[COLLATERAL] LOAD_HYPOTHEC_COLLATERALS_AGAIN',
  async (instanceName, { dispatch, rejectWithValue, extra, getState }) => {
    const params = getState().collateral.hypothecCollaterals.instance[instanceName].searchParams
    const result = await extra.api.collateral.collateralSearch(params)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue(result.error.message)
    }
    return {
      instanceName: instanceName,
      data: result.data,
    }

    // return result.data
  },
)

export const resetHypothecCollaterals = createAction('[COLLATERAL] LOAD_HYPOTHEC_COLLATERALS')

export const destroyCollateralsSearch = createAction('[COLLATERAL] DESTROY_HYPOTHEC_COLLATERALS')

export const collateralView = createAsyncThunk<HypothecCollateralsRequestServerData, CollateralViewParams, ThunkApi>(
  '[COLLATERAL] VIEW_COLLATERALS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralView(params)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue('Failed to view collaterals')
    }

    return result.data
  },
)

export const collateralprint = createAsyncThunk<HypothecCollateralsRequestServerData, CollateralViewParams, ThunkApi>(
  '[COLLATERAL] VIEW_COLLATERALS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralPrint(params)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue('Failed to view collaterals')
    }

    return result.data
  },
)

export const collateralStateHistory = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralViewParams,
  ThunkApi
>('[COLLATERAL] VIEW_COLLATERALS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralStateHistory(params)

  if (!result.success) {
    dispatch(LKModalActions.openError(translateBase(result.error.message)))

    return rejectWithValue('Failed to view collaterals')
  }

  return result.data
})

export const collateralNewEstimation = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralViewParams,
  ThunkApi
>('[COLLATERAL] VIEW_COLLATERALS_NEW_ESTIMATIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralNewEstimation(params)

  if (!result.success) {
    dispatch(LKModalActions.openError(translateBase(result.error.message)))

    return rejectWithValue('Failed to view collaterals new estimations')
  }

  return result.data
})

export const collateralAdd = createAsyncThunk<HypothecCollateralsRequestServerData, CollateralAddParams, ThunkApi>(
  '[COLLATERAL] ADD_COLLATERAL',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralAdd(params)
    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(`LKMESSAGE-${result.error.message}`)))

      return rejectWithValue('Failed to add collateral')
    }

    return result.data
  },
)

export const collateralAddPermission = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralAddPermissionParams,
  ThunkApi
>('[COLLATERAL] ADD_COLLATERAL_PERMISSION', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralAddPermission(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }

  return result.data
})

export const collateralDeletePermission = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralDeletePermissionParams,
  ThunkApi
>('[COLLATERAL] DELETE_COLLATERAL_PERMISSION', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralDeletePermission(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }

  return result.data
})

export const collateralOperations = createAsyncThunk<any, CollateralOperationsParams, ThunkApi>(
  '[COLLATERAL] DO_COLLATERAL_OPERATIONS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralOperations(params)

    if (!result.success) {
      return rejectWithValue(result.error.message)
    }
    return result.data
  },
)

export const collateralVisa = createAsyncThunk<HypothecCollateralsRequestServerData, CollateralVisaParams, ThunkApi>(
  '[COLLATERAL] DO_COLLATERAL_VISA',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralVisa(params)

    if (!result.success) {
      return rejectWithValue(result.error.message)
    }
    return result.data
  },
)

export const collateralUndoVisa = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralUndoVisaParams,
  ThunkApi
>('[COLLATERAL] UNDO_COLLATERAL_VISA', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralUndoVisa(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }
  return result.data
})

export const collateralUpdateAgreementNumber = createAsyncThunk<void, CollateralUpdateAgreementNumberParams, ThunkApi>(
  '[COLLATERAL] UPDATE_COLLATERAL_AGREEMENT_NUMBER',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralUpdateAgreementNumber(params)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue('Failed to update collateral update number')
    }
  },
)

export const collateralEditState = createAsyncThunk<void, CollateralEditStateParams, ThunkApi>(
  '[COLLATERAL] EDIT_COLLATERAL_STATE',
  //@ts-ignore
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralEditState(params)

    if (!result.success) {
      // dispatch(LKModalActions.openError(translateBase(result.error.message)))

      // return rejectWithValue('Failed to edit collateral state')
      return { code: 1, message: result.error.message }
    }
  },
)

export const collateralEditDetailState = createAsyncThunk<void, CollateralEditDetailStateParams, ThunkApi>(
  '[COLLATERAL] EDIT_COLLATERAL_DETAIL_STATE',
  //@ts-ignore
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralEditDetailState(params)

    if (!result.success) {
      // dispatch(LKModalActions.openError(translateBase(result.error.message)))

      // return rejectWithValue('Failed to edit collateral detail state')
      return { code: 1, message: result.error.message }
    }
  },
)

export const collateralUpdateDesc = createAsyncThunk<void, CollateralUpdateDescParams, ThunkApi>(
  '[COLLATERAL] UPDATE_COLLATERAL_DESCRIPTION',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralUpdateDesc(params)

    if (!result.success) {
      dispatch(LKModalActions.openError(translateBase(result.error.message)))

      return rejectWithValue('Failed to update collateral description')
    }
  },
)

export const collateralManagementViewSessions = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralManagementViewSessionsParams,
  ThunkApi
>('[COLLATERAL] VIEW_COLLATERAL_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementViewSessions(params)

  if (!result.success) {
    dispatch(LKModalActions.openError(translateBase(result.error.message)))

    return rejectWithValue('Failed to view collateral sessions')
  }

  return result.data
})

export const collateralManagementPrintSessions = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralManagementPrintSessionsParams,
  ThunkApi
>('[COLLATERAL] PRINT_COLLATERAL_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementPrintSessions(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }

  return result.data
})

export const collateralManagementViewWorklist = createAsyncThunk<
  HypothecCollateralsRequestServerData,
  CollateralManagementViewWorklistParams,
  ThunkApi
>('[COLLATERAL] VIEW_COLLATERAL_WORKLIST', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementViewWorklist(params)
  if (!result.success) {
    // dispatch(LKModalActions.openError(translateBase(result.error.message)))

    return rejectWithValue(result.error.message)
  }

  return result.data
})

export const collateralManagementLoadWorklist = createAsyncThunk<
  void,
  CollateralManagementLoadWorklistParams,
  ThunkApi
>('[COLLATERAL] LOAD_COLLATERAL_WORKLIST_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementLoadWorklist(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }
})

export const printCollateralManagement = createAsyncThunk<
  any,
  CollateralManagementLoadWorklistParams & { action: string; page: string },
  ThunkApi
>('[COLLATERAL] PRINT_COLLATERAL_MANAGEMENT', async (params, { dispatch, extra, rejectWithValue }) => {
  const printParams = {
    ...params,
    action: CollateralManagementViewActions.PRINTSESSION,
  }

  printParams.page && delete printParams.page

  const result = await extra.api.collateral.printCollateralManagement(printParams)
  if (!result.success) {
    return rejectWithValue(result.error.message)
  }
  return result.data
})

export const collateralManagementClearWorklist = createAsyncThunk<
  void,
  CollateralManagementClearWorklistParams,
  ThunkApi
>('[COLLATERAL] CLEAR_COLLATERAL_WORKLIST_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementClearWorklist(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }
})

export const collateralManagementAddWorklist = createAsyncThunk<void, CollateralManagementAddWorklistParams, ThunkApi>(
  '[COLLATERAL] ADD_COLLATERAL_WORKLIST_SESSIONS',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.collateral.collateralManagementAddWorklist(params)

    if (!result.success) {
      return rejectWithValue(result.error.message)
    }
  },
)

export const collateralManagementDeleteWorklist = createAsyncThunk<
  void,
  CollateralManagementDeleteWorklistParams,
  ThunkApi
>('[COLLATERAL] DELETE_COLLATERAL_WORKLIST_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementDeleteWorklist(params)

  if (!result.success) {
    dispatch(LKModalActions.openError(translateBase(result.error.message)))

    return rejectWithValue('Failed to delete collateral worklist')
  }
})

export const collateralManagementProcessWorklist = createAsyncThunk<
  any,
  CollateralManagementProcessWorklistParams,
  ThunkApi
>('[COLLATERAL] PROCESS_COLLATERAL_WORKLIST_SESSIONS', async (params, { dispatch, extra, rejectWithValue }) => {
  const result = await extra.api.collateral.collateralManagementProcessWorklist(params)

  if (!result.success) {
    return rejectWithValue(result.error.message)
  }
  return result.data
})
